import React from "react";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { AppLink, LinkAnchor } from "../../../components/ui/link";
import {
  formatAdminTableDate,
  type AdminTableDateOptions,
} from "../../../lib/formatAdminTableDate";
import { AdminBusinessBillingForm } from "./AdminBusinessBillingForm";
import { AdminChatsSection } from "./AdminChatsSection";
import { AdminDeleteForm } from "./AdminDeleteForm";
import { AdminQuotesTable } from "./AdminQuotesTable";
import type { AdminAccountDetailPayload, AdminRouteMap } from "../types";

type AdminUserDetailPageProps = {
  detail: AdminAccountDetailPayload;
  billingStatusOptions: readonly string[];
  dateOptions: AdminTableDateOptions;
  csrfToken: string;
  currentUserId: number | null;
  adminRoutes: AdminRouteMap;
};

export const AdminUserDetailPage: React.FC<AdminUserDetailPageProps> = ({
  detail,
  billingStatusOptions,
  dateOptions,
  csrfToken,
  currentUserId,
  adminRoutes,
}) => {
  const { user, business, quotes, chats, deletionImpact } = detail;
  const title =
    user.ownerName?.trim() ||
    (business?.businessName ?? business?.subdomain) ||
    user.email;
  const subtitle = `${user.email} · user #${user.id}`;
  const organizationNames = deletionImpact.organizationsToDestroy
    .map((organization) => organization.name)
    .join(", ");
  const organizationDestroyConfirmMessage =
    deletionImpact.requiresOrganizationDestroy
      ? `This user is the last owner or admin for ${organizationNames}. Deleting them will permanently delete ${deletionImpact.organizationsToDestroy.length === 1 ? "that organization" : "those organizations"} (website, jobs, billing, and team data). This cannot be undone. Continue?`
      : undefined;

  return (
    <DashboardPageLayout
      title={title}
      subtitle={subtitle}
      headerActions={
        <AppLink
          to={adminRoutes.adminOrganizations}
          variant="muted"
          className="text-sm"
        >
          ← All organizations
        </AppLink>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>Business</CardTitle>
          <CardDescription>
            Live site, billing, and account actions for this signup.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {business ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Display name
                  </p>
                  <p className="text-sm font-medium mt-1">
                    {business.businessName ?? business.subdomain}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Subdomain
                  </p>
                  <p className="text-sm font-mono mt-1">{business.subdomain}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Trial ends
                  </p>
                  <p className="text-sm mt-1 text-muted-foreground">
                    {business.trialEndsAt
                      ? formatAdminTableDate(business.trialEndsAt, dateOptions)
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Billing status
                  </p>
                  <AdminBusinessBillingForm
                    action={adminRoutes.adminUpdateBusinessBilling.replace(
                      ":id",
                      String(business.id),
                    )}
                    csrfToken={csrfToken}
                    currentStatus={business.billingStatus}
                    statusOptions={billingStatusOptions}
                  />
                </div>
              </div>
              <div>
                <LinkAnchor
                  variant="premium"
                  className="text-sm"
                  href={business.siteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open live site
                </LinkAnchor>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This user does not have a business or organization yet.
              </p>
              {(user.trialStartedAt || user.trialEndsAt) && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      User trial started
                    </p>
                    <p className="text-sm mt-1 text-muted-foreground">
                      {user.trialStartedAt
                        ? formatAdminTableDate(user.trialStartedAt, dateOptions)
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      User trial ends
                    </p>
                    <p className="text-sm mt-1 text-muted-foreground">
                      {user.trialEndsAt
                        ? formatAdminTableDate(user.trialEndsAt, dateOptions)
                        : "—"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="pt-4 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Danger zone
            </p>
            {currentUserId != null && user.id === currentUserId ? (
              <span className="text-sm text-muted-foreground">
                You cannot delete your own account.
              </span>
            ) : user.admin ? (
              <span className="text-sm text-muted-foreground">
                Platform admin accounts cannot be deleted.
              </span>
            ) : (
              <AdminDeleteForm
                action={adminRoutes.adminDestroyUser.replace(
                  ":id",
                  String(user.id),
                )}
                csrfToken={csrfToken}
                confirmMessage={`Delete user ${user.email}? This removes their business, quotes, and related data.`}
                organizationDestroyConfirmMessage={
                  organizationDestroyConfirmMessage
                }
              />
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quotes</CardTitle>
          <CardDescription>
            Quote requests for this business ({quotes.length} shown).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {quotes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No quotes yet.</p>
          ) : (
            <AdminQuotesTable
              quotes={quotes}
              dateOptions={dateOptions}
              csrfToken={csrfToken}
              adminRoutes={adminRoutes}
              showBusinessColumn={false}
            />
          )}
        </CardContent>
      </Card>

      <AdminChatsSection
        chats={chats}
        dateOptions={dateOptions}
        csrfToken={csrfToken}
        adminRoutes={adminRoutes}
        accountScoped
      />
    </DashboardPageLayout>
  );
};
